import React, { useState } from "react";
import {
  ActionTab,
  AuthenticationTab,
  BodyParamsTab,
  ErrorTab,
  HeadersTab,
  PathParamsTab,
  QueryParamsTab,
  SuccessTab
} from "./WebHookTabs";
import CustomJS from "../../../components/BuilderComponents/CustomJS";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import Input from "../../../components/FormComponents/Input";
import BuilderTabs from "../../../Layouts/BuilderTabs";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastSuccess } from "../../../utils/toastUtils";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";

const WebhookApiEditorLatest = ({ file, selectedApiData, setSelectedApiData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { markClean, isDirty } = useDirtyChecker(selectedApiData);

  const getMethodColors = (method) => {
    const colors = {
      get: { borderColor: "#10b981", color: "#10b981" },
      post: { borderColor: "#3b82f6", color: "#3b82f6" },
      put: { borderColor: "#f59e0b", color: "#f59e0b" },
      patch: { borderColor: "#8b5cf6", color: "#8b5cf6" },
      delete: { borderColor: "#ef4444", color: "#ef4444" }
    };
    return colors[method] || { borderColor: "#d1d5db", color: "#374151" };
  };

  const methodColors = getMethodColors(selectedApiData?.method || "post");

  const handleInputChange = (name, value) => {
    const copyObject = { ...selectedApiData };
    copyObject[name] = value;
    setSelectedApiData(copyObject);
  };

  const handlePreProcessingChange = (data) => {
    const copyApiData = JSON.parse(JSON.stringify(selectedApiData));
    copyApiData.pre_processing = data;
    setSelectedApiData(copyApiData);
  };

  const handlePostProcessingChange = (data) => {
    const copyApiData = JSON.parse(JSON.stringify(selectedApiData));
    copyApiData.post_processing = data;
    setSelectedApiData(copyApiData);
  };

  const handleSaveWebhook = async () => {
    try {
      setIsSaving(true);
      const payload = {
        ...selectedApiData,
        name: file.name
      };
      const response = await axiosInstance.put(`/webhook-config/${file.id}`, payload);
      toastSuccess(response?.message || "Webhook Saved Successfully");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const apiOptions = [
    { id: "get", name: "GET" },
    { id: "post", name: "POST" },
    { id: "put", name: "PUT" },
    { id: "patch", name: "PATCH" },
    { id: "delete", name: "DELETE" }
  ];

  const inputTabs = [
    {
      label: "Query Params",
      content: <QueryParamsTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    },
    {
      label: "Path Params",
      content: <PathParamsTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    },
    {
      label: "Body Params",
      content: <BodyParamsTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    },
    {
      label: "Authentication",
      content: (
        <AuthenticationTab id={file.id} selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
      )
    },
    {
      label: "Headers",
      content: <HeadersTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    }
  ];

  const actionTabs = [
    {
      label: "Actions",
      content: <ActionTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    },
    {
      label: "Advanced",
      content: (
        <div>
          <div className="mt_3">
            <CustomJS
              label="Pre Processing"
              inputString={selectedApiData?.pre_processing || ""}
              handleChange={handlePreProcessingChange}
              tableID={selectedApiData?.sm_id || selectedApiData?.rdbms_id || ""}
              table_type={selectedApiData?.table_type}
              showFullScreenIcon={true}
            />
          </div>
          <div className="mt_3">
            <CustomJS
              label="Post Processing"
              inputString={selectedApiData?.post_processing || ""}
              handleChange={handlePostProcessingChange}
              tableID={selectedApiData?.sm_id || selectedApiData?.rdbms_id || ""}
              table_type={selectedApiData?.table_type}
              showFullScreenIcon={true}
            />
          </div>
        </div>
      )
    }
  ];

  const messageTabs = [
    {
      label: "Success",
      content: <SuccessTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    },
    {
      label: "Error",
      content: <ErrorTab selectedApiData={selectedApiData} handleInputChange={handleInputChange} />
    }
  ];

  return (
    <div>
      <GenericActionBar
        path={
          <div>
            <em>API</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveWebhook
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />
      <div className="page_main_section pb_5">
        <div className="w_75 m_auto">
          <div className="builder_form">
            {/* method and path */}
            <div className="d_flex gap_2 mb_3 w_100">
              <div
                style={{
                  width: "15%"
                }}
              >
                <SingleSelectBox
                  selectedData={selectedApiData?.method || "post"}
                  options={apiOptions}
                  onChange={(obj) => handleInputChange("method", obj.id)}
                  placeholder="POST"
                  label="Select Method"
                  containerStyle={{
                    "--method-border-color": methodColors.borderColor,
                    "--method-text-color": methodColors.color
                  }}
                />
              </div>
              <div style={{ width: "85%" }} className="d_flex flex_column gap_2">
                <label className="builder_label">Enter Path</label>
                <div className="url_input_group">
                  <div className="url_prefix_display">
                    <MaterialIcon
                      name="lock"
                      className="cursor_pointer"
                      style={{ fontSize: "16px", paddingRight: "4px" }}
                    />
                    https://dev-apps.getzino.site/api/v1/webhook/
                  </div>
                  <Input
                    type="text"
                    name="url_path"
                    value={selectedApiData?.url_path}
                    onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                    containerClassName="url_path_container"
                    inputClassName="url_path_input"
                    placeholder="path"
                  />
                </div>
              </div>
              <div className="d_flex align_center gap_2 mt_auto mb_1">
                {selectedApiData?.api_keys.length > 0 || selectedApiData?.zino_auth ? (
                  <MaterialIcon name="lock" style={{ color: "#28a745" }} />
                ) : (
                  <MaterialIcon name="lock_open" style={{ color: "#dc3545" }} />
                )}
              </div>
            </div>
            {/* input tabs */}
            <div>
              <BuilderTabs
                tabs={inputTabs}
                tabsClass="w_fit custom-hook-tab"
                containerClass="bg_transparent w_fit non-sticky"
              />
              <BuilderTabs
                tabs={actionTabs}
                tabsClass="w_fit custom-hook-tab mt_2"
                containerClass="bg_transparent w_fit non-sticky"
              />
              <BuilderTabs
                tabs={messageTabs}
                tabsClass="w_fit custom-hook-tab mt_2"
                containerClass="bg_transparent w_fit non-sticky"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookApiEditorLatest;
